import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRAND_SUPPLY_LABELS,
  FRANCHISE_PRICE_MODE_LABELS,
  type FranchiseAccountDTO,
  type FranchiseInvoiceDTO,
  type FranchiseOrderDTO,
  type Paged,
  type FranchiseRoyaltyDTO,
  type MyFranchiseDTO,
} from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Dialog, Kpi, PageHeader, Select, Skeleton, useToast } from '@bifeai/ui';
import { ChartColumn, HandCoins, Plus, Settings, Truck, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import {
  AccountTable,
  balanceText,
  InvoicesTable,
  LinesEditor,
  newLine,
  OrdersTable,
  Pager,
  RangeFilter,
  useListRange,
  parseLines,
  periodLabel,
  useStockProducts,
  type DraftLine,
} from './common';

/**
 * Mi franquicia: la relación del franquiciado con la marca. Las reglas que puso la marca,
 * los pedidos de mercadería, la cuenta corriente y las regalías.
 */
export function MyFranchisePage() {
  const { can, me: session } = useSession();
  const arca = session.tenant.modules.includes('ARCA');
  const me = useQuery({ queryKey: ['franchise', 'me'], queryFn: () => api.get<MyFranchiseDTO>('/franchise') });
  const ordersRange = useListRange();
  const accountRange = useListRange();
  const invoicesRange = useListRange();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const orders = useQuery({
    queryKey: ['franchise', 'orders', ...ordersRange.key],
    queryFn: () => api.get<Paged<FranchiseOrderDTO>>(`/franchise/orders?${ordersRange.qs}`),
    enabled: can('transfers.manage'),
    placeholderData: keepPreviousData,
  });
  const account = useQuery({
    queryKey: ['franchise', 'account', ...accountRange.key],
    queryFn: () => api.get<FranchiseAccountDTO>(`/franchise/account?${accountRange.qs}`),
    placeholderData: keepPreviousData,
  });
  const royalties = useQuery({
    queryKey: ['franchise', 'royalties', year],
    queryFn: () => api.get<FranchiseRoyaltyDTO[]>(`/franchise/royalties?year=${year}`),
    placeholderData: keepPreviousData,
  });
  const invoices = useQuery({
    queryKey: ['franchise', 'invoices', ...invoicesRange.key],
    queryFn: () => api.get<Paged<FranchiseInvoiceDTO>>(`/franchise/invoices?${invoicesRange.qs}`),
    enabled: arca,
    placeholderData: keepPreviousData,
  });
  const nowPeriod = `${thisYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [ordering, setOrdering] = useState(false);
  const [canceling, setCanceling] = useState<FranchiseOrderDTO | null>(null);

  const f = me.data;
  if (!f) return <Skeleton style={{ height: 320 }} />;
  const s = f.settings;
  return (
    <>
      <PageHeader title="Mi franquicia" subtitle={`${f.name} · franquicia de ${f.brandName}`} />
      <div className="bf-kpi-grid">
        <Kpi label="Ventas del mes" value={formatMoney(f.salesMonthCents)} icon={<ChartColumn />} />
        <Kpi label={`Regalías del mes (${s.royaltyPct} %)`} value={formatMoney(f.royaltyMonthCents)} icon={<HandCoins />} tone="amber" />
        <Kpi label="Cuenta con la marca" value={balanceText(f.balanceCents)} icon={<Wallet />} tone={f.balanceCents > 0 ? 'red' : 'green'} />
        <Kpi label="Pedidos sin enviar" value={f.pendingOrders} icon={<Truck />} tone="green" />
      </div>

      <Card>
        <CardHeader title="Lo que definió la marca" subtitle="Estas reglas las pone la marca. Si necesitás cambiar algo, hablalo con ellos." />
        <CardBody>
          <ul className="bf-plain-list">
            <li>
              <strong>Precios:</strong> {FRANCHISE_PRICE_MODE_LABELS[s.priceMode]}
              {s.priceMode === 'RANGE' ? ` (hasta ${s.priceRangePct} % arriba o abajo)` : ''}.
            </li>
            <li>
              <strong>Mercadería:</strong> {BRAND_SUPPLY_LABELS[s.brandSupply]}.
            </li>
            <li>
              <strong>Proveedores:</strong>{' '}
              {[s.approvedSuppliers && 'los homologados por la marca', s.ownSuppliers && 'los tuyos'].filter(Boolean).join(' y ') ||
                'no carga compras a proveedores'}
              .
            </li>
            <li>
              <strong>Regalías:</strong> {s.royaltyPct} % de las ventas del mes.
            </li>
            {arca && (
              <li>
                <strong>Facturas:</strong>{' '}
                {s.autoInvoice
                  ? 'la marca te factura por ARCA, a tu CUIT, la mercadería que te vende y las regalías. Entran solas en Compras.'
                  : 'la marca te factura cuando lo emite desde su cuenta.'}
              </li>
            )}
          </ul>
          {can('franchise.accounts') && (
            <p className="bf-hint" style={{ marginBottom: 0 }}>
              Tu Mercado Pago y tu facturación ARCA se configuran en{' '}
              <Link to="/configuracion">
                <Settings size={14} aria-hidden /> Cuentas de la franquicia
              </Link>
              .
            </p>
          )}
        </CardBody>
      </Card>

      {s.brandSupply !== 'NONE' && can('transfers.manage') && (
        <Card>
          <CardHeader
            title="Pedidos y envíos de la marca"
            subtitle={
              s.brandSupply === 'ORDER'
                ? 'Lo que pediste y lo que te mandó la marca. Se carga en tu cuenta corriente al precio que te pase.'
                : 'Lo que pediste y lo que te mandó la marca, sin cargo.'
            }
            actions={
              <Button variant="primary" icon={<Plus />} onClick={() => setOrdering(true)}>
                Nuevo pedido
              </Button>
            }
          />
          <CardBody flush>
            <div style={{ padding: '0 16px 12px' }}>
              <RangeFilter range={ordersRange} />
            </div>
            {!orders.data ? (
              <Skeleton style={{ height: 100, margin: 16 }} />
            ) : (
              <>
                <OrdersTable
                  orders={orders.data.items}
                  showBranch={false}
                  side="franchise"
                  onOpen={setCanceling}
                  actionFor={(o) => (o.status === 'REQUESTED' ? 'Cancelar' : null)}
                />
                <Pager range={ordersRange} total={orders.data.total} />
              </>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Cuenta corriente con la marca"
          subtitle="Debe: mercadería, regalías y cargos. Haber: tus pagos (los registra la marca) y las notas de crédito o descuentos que te hace."
        />
        <CardBody>
          <div style={{ marginBottom: 12 }}>
            <RangeFilter range={accountRange} />
          </div>
          {account.data ? (
            <>
              <AccountTable account={account.data} base="/franchise/invoices" />
              <Pager range={accountRange} total={account.data.total ?? 0} />
            </>
          ) : (
            <Skeleton style={{ height: 100 }} />
          )}
        </CardBody>
      </Card>

      {arca && (
        <Card>
          <CardHeader
            title="Facturas de la marca"
            subtitle="Lo que la marca te facturó por ARCA con su CUIT. Cada factura también queda como compra al proveedor “la marca” (pagada por esta cuenta corriente)."
          />
          <CardBody flush>
            <div style={{ padding: '0 16px 12px' }}>
              <RangeFilter range={invoicesRange} />
            </div>
            {invoices.data ? (
              <>
                <InvoicesTable invoices={invoices.data.items} base="/franchise/invoices" showBranch={false} />
                <Pager range={invoicesRange} total={invoices.data.total} />
              </>
            ) : (
              <Skeleton style={{ height: 100, margin: 16 }} />
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Regalías"
          subtitle="Lo que corresponde cada mes según tus ventas."
          actions={
            <Select aria-label="Año" value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
              {[thisYear, thisYear - 1, thisYear - 2, thisYear - 3].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          }
        />
        <CardBody flush>
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th className="bf-num">Ventas</th>
                  <th className="bf-num">Regalías</th>
                  <th className="bf-num">IVA</th>
                  <th className="bf-num">Total</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {royalties.data?.map((r) => (
                  <tr key={r.period}>
                    <td className="bf-cell-main">{periodLabel(r.period)}</td>
                    <td className="bf-num">{formatMoney(r.salesCents)}</td>
                    <td className="bf-num">{formatMoney(r.royaltyCents)}</td>
                    <td className="bf-num">{r.vatCents ? formatMoney(r.vatCents) : '—'}</td>
                    <td className="bf-num">{formatMoney(r.totalCents)}</td>
                    <td>
                      {r.charged ? (
                        <Badge tone="success">En la cuenta</Badge>
                      ) : r.period === nowPeriod ? (
                        <Badge>Mes en curso</Badge>
                      ) : r.royaltyCents > 0 ? (
                        <Badge tone="warning">La marca todavía no la cargó</Badge>
                      ) : (
                        <span className="bf-cell-sub">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {ordering && <NewOrderDialog onClose={() => setOrdering(false)} />}
      {canceling && <CancelDialog order={canceling} onClose={() => setCanceling(null)} />}
    </>
  );
}

function NewOrderDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const products = useStockProducts();
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function send() {
    const items = parseLines(lines, products.data, false);
    if (!items) return setError('Completá cada producto con su cantidad.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/franchise/orders', { items: items.map((i) => ({ productId: i.productId, qty: i.qty })), notes: notes || null });
      await qc.invalidateQueries({ queryKey: ['franchise'] });
      toast.success('Pedido enviado a la marca');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nuevo pedido a la marca"
      description="Cargá los productos y las cantidades (kilos o unidades)."
    >
      <div className="bf-form">
        <LinesEditor lines={lines} onChange={setLines} withPrice={false} />
        <textarea
          className="bf-input"
          placeholder="Nota para la marca (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={busy} onClick={() => void send()}>
            Enviar pedido
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CancelDialog({ order, onClose }: { order: FranchiseOrderDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Pedido #${order.number}`} description="Todavía no se envió. ¿Lo cancelás?">
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose}>Volver</Button>
          <Button
            variant="danger"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/franchise/orders/${order.id}/cancel`);
                await qc.invalidateQueries({ queryKey: ['franchise'] });
                onClose();
              } catch (err) {
                setError(errorMessage(err));
                setBusy(false);
              }
            }}
          >
            Cancelar pedido
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
